import { IJob } from '@medely/types';
import { Style } from '@medely/ui-kit';
import { formatCappedCount, isNonEmptyArray } from '@mp/utils';
import ExpiringConditions from '@mp/components/taskCards/ExpiringConditions';
import React from 'react';
import { Platform, RenderCardMap } from './nextStepsHelpers';
import { NextStepsTemplate } from '.';
import {
  ApprovedRateChangeCard,
  CampaignsNextStepCards,
  GetReady,
  sortApprovedRateChangesByDecidedAt,
  ProPoolInvite,
  ProPoolOnboarding,
  ProPoolOrientation,
  SchedulerPreferencesCard,
  SetYourselfUp,
  ShiftSwapCard,
} from '../taskCards';
import { useSchedulingPreferencesData } from '@mp/components/schedulingPreferences';
import {
  PendingProPoolInvite,
  PendingProPoolOnboarding,
  ProfessionalTodos,
} from '@mp/hooks/useProfessionalTodos';
import { getNextStepsIcons } from './nextStepsIcons';
import {
  TrackNextStepCardClickPayload,
  useCurrentUser,
  useGate,
  type useRecommendedCredentialsJobCountsSummary,
} from '@mp/hooks';
import { ReescreningAutomationCard } from '../taskCards/ReescreningAutomationCard';
import { gateNames } from '@mp/constants';
import { ShiftRequestStatus, type RateChangeTodo } from '@mp/graphql/generated/graphql';

export type FullProfessionalTodosKeys =
  | 'campaigns_status'
  | 'w2_interest_card'
  | 'disputes_waiting_on_professional'
  | 'active_jobs_requesting_to_work_facility_ids'
  | 'enable_location_services'
  | 'unsigned_addendum_assignment_ids'
  | 'extension_offers'
  | 'assignment_offers'
  | 'pending_pro_pool_invite'
  | 'pending_pro_pool_onboarding'
  | 'pending_pro_pool_orientation'
  | 'days_until_debit_card_expires'
  | 'has_position_with_completed_onboarding'
  | 'assignments_missing_conditions'
  | 'expiring_conditions'
  | 'rescreening'
  | 'available_for_extensions'
  | 'finished_jobs_count'
  | 'set_yourself_up'
  | 'scheduling_preferences'
  | 'get_ready'
  | 'missing_w2_per_diem_onboarding'
  | 'missing_premier_credentials'
  | 'permanent_placement_offers'
  | 'pending_shift_swaps'
  | 'approved_rate_changes';

interface BuildFullTodosOptions {
  todos: ProfessionalTodos;
  bookedJobs: IJob[];
  requestedJobs: IJob[];
  isGeolocationEnabled: boolean;
  expiringConditionsInfo?: { severity: string; title: string } | null;
  isW2OnboardingMissing?: boolean;
}
const FINISHED_JOBS_THRESHOLD = 5;
export function buildFullTodos({
  todos,
  bookedJobs,
  requestedJobs,
  isGeolocationEnabled,
  expiringConditionsInfo,
  isW2OnboardingMissing,
}: BuildFullTodosOptions) {
  const bookedJobsCount = todos?.booked_jobs_count ?? 0;
  const finishedJobsCount = todos?.finished_jobs_count ?? 0;
  const proHasOnboarded = todos?.has_position_with_completed_onboarding;
  const enable_location_services =
    isNonEmptyArray(bookedJobs) &&
    !isGeolocationEnabled &&
    bookedJobs.some((job) => job?.location?.enable_geo_fence);

  const active_jobs_requesting_to_work_facility_ids =
    (todos?.active_jobs_requesting_to_work_facility_ids as number[] | undefined)?.filter(
      (id: number) => requestedJobs.some((job) => job?.location?.id === id),
    ) ?? [];
  const shouldShowFinishedJobsCount = finishedJobsCount < FINISHED_JOBS_THRESHOLD;
  const shouldShowSetYourselfUp = !!(proHasOnboarded && bookedJobsCount === 0);
  const finalFinishedJobsCount =
    shouldShowFinishedJobsCount && !shouldShowSetYourselfUp ? finishedJobsCount : null;
  const finalSetYourselfUp = shouldShowSetYourselfUp;

  const { preferencesLastUpdatedAt, shouldShowSchedulingPreferences } =
    useSchedulingPreferencesData();

  const approvedRateChangesRaw =
    todos?.approved_rate_changes?.filter(
      (row): row is RateChangeTodo => row?.status === ShiftRequestStatus.Approved,
    ) ?? [];

  return {
    ...todos,
    enable_location_services,
    active_jobs_requesting_to_work_facility_ids,
    get_ready: !!(bookedJobsCount > 0 && finishedJobsCount < FINISHED_JOBS_THRESHOLD),
    finished_jobs_count: finalFinishedJobsCount,
    set_yourself_up: finalSetYourselfUp,
    expiring_conditions: expiringConditionsInfo ?? undefined,
    isW2OnboardingMissing: !!isW2OnboardingMissing,
    missing_w2_per_diem_onboarding: !!isW2OnboardingMissing,
    scheduling_preferences: shouldShowSchedulingPreferences && !preferencesLastUpdatedAt,
    approved_rate_changes: approvedRateChangesRaw,
  };
}

type RecommendedCredentialsSummary = ReturnType<typeof useRecommendedCredentialsJobCountsSummary>;

const formatRecommendedCredentialsFootnote = ({
  openJobsCount,
  recentlyPostedJobsCount,
}: RecommendedCredentialsSummary) => {
  const openJobs =
    openJobsCount > 0 &&
    (openJobsCount === 1 ? '1 shift open' : `${formatCappedCount(openJobsCount)} shifts open`);
  const recentlyPostedJobs =
    recentlyPostedJobsCount > 0 && `${formatCappedCount(recentlyPostedJobsCount)} posted recently`;

  if (!openJobs && !recentlyPostedJobs) {
    return undefined;
  }

  return (
    <>
      {openJobs && <Style bold>{openJobs}</Style>}
      {openJobs && recentlyPostedJobs && ' • '}
      {recentlyPostedJobs}
    </>
  );
};

export function getRenderCardMap<T extends Record<string, any>>(
  platform: Platform,
  options: {
    onTrackClickPayload?: (payload: TrackNextStepCardClickPayload) => void;
    isProPoolEnabled?: boolean;
    isProPoolAutomationEnabled?: boolean;
    isAgencyPro?: boolean;
    recommendedConditions?: any[];
    recommendedCredentials?: RecommendedCredentialsSummary;
    openWebRedirect?: () => void;
    handleAllow?: () => void;
    proType?: 'marketplace' | 'agency' | 'irp';
  },
): RenderCardMap<T> {
  const reescreeningGate = useGate(gateNames.rescreening_automation);
  const isShiftRequestGateOpen = useGate(gateNames.direct_shift_release_pro);
  const { isAgency } = useCurrentUser();
  const icons = getNextStepsIcons();
  const shouldRenderFor = (
    allowedTypes: ('marketplace' | 'agency' | 'irp')[],
    currentType: 'marketplace' | 'agency' | 'irp' | undefined,
  ): boolean => {
    return allowedTypes.includes(currentType ?? 'marketplace');
  };

  const map: RenderCardMap<T> = {};

  map['expiring_conditions' as keyof T] = (data) =>
    shouldRenderFor(['marketplace', 'agency'], options.proType) && data ? (
      <ExpiringConditions title={data.title} severity={data.severity} />
    ) : null;

  map['rescreening' as keyof T] = (data) =>
    !!data && reescreeningGate ? <ReescreningAutomationCard key="rescreening" /> : null;

  map['assignment_offers' as keyof T] = (data) =>
    !isAgency && isNonEmptyArray(data) ? (
      <NextStepsTemplate
        key="assignment_offers"
        content="You have an assignment offer ready to review and accept."
        badge="Needs Attention"
        icon={icons.triangleExclamation}
        to={platform === 'web' ? '/my-assignments' : { pathname: 'Assignments' }}
        color="error"
      />
    ) : null;

  map['available_for_extensions' as keyof T] = (assignmentIds) =>
    Array.isArray(assignmentIds)
      ? assignmentIds.map((assignmentId: number) => (
          <NextStepsTemplate
            key={`available_for_extensions-${assignmentId}`}
            content="Let us know if you are interested in extending or finding new work"
            title="Your assignment is nearing completion"
            icon={icons.calendarDay}
            to={
              platform === 'web'
                ? {
                    pathname: `/assignments/${assignmentId}/details`,
                    state: { opensExtensionDialog: true },
                  }
                : {
                    pathname: 'AssignmentDetails',
                    state: {
                      id: assignmentId,
                      opensExtensionDialog: true,
                    },
                  }
            }
            color="default"
          />
        ))
      : null;

  map['extension_offers' as keyof T] = (data) =>
    !isAgency && isNonEmptyArray(data) ? (
      <NextStepsTemplate
        key="extension_offers"
        content="You have an assignment extension offer ready to review and accept."
        badge="Needs Attention"
        icon={icons.triangleExclamation}
        to={
          platform === 'web'
            ? `/available/assignments/${data[0]}`
            : { pathname: 'AvailableAssignmentDetails', state: { id: data[0] } }
        }
        color="error"
      />
    ) : null;

  map['unsigned_addendum_assignment_ids' as keyof T] = (data) =>
    isNonEmptyArray(data) ? (
      <NextStepsTemplate
        key="unsigned_addendum_assignment_ids"
        content="Please review and sign your latest assignment addendum."
        badge="Needs Attention"
        icon={icons.triangleExclamation}
        to={
          platform === 'web'
            ? `/assignments/${data[0]}/contracts`
            : { pathname: 'AssignmentDetails', state: { id: data[0] } }
        }
        color="error"
      />
    ) : null;

  map['has_position_with_completed_onboarding' as keyof T] = (data) =>
    !data ? (
      <NextStepsTemplate
        key="has_position_with_completed_onboarding"
        content="Complete your onboarding to begin booking shifts."
        badge="Needs Attention"
        icon={icons.triangleExclamation}
        to={platform === 'web' ? '/onboarding' : { pathname: 'Onboarding' }}
        color="error"
      />
    ) : null;

  map['missing_premier_credentials' as keyof T] = (isMissingRecommended) => {
    if (!shouldRenderFor(['marketplace'], options.proType)) {
      return null;
    }

    const recommendedCredentials = options.recommendedCredentials;
    const to =
      platform === 'web'
        ? '/credentials/recommended'
        : { pathname: 'RootCredentials', state: { type: 'recommended' } };

    if (recommendedCredentials?.isEnabled) {
      return recommendedCredentials.conditionCount ? (
        <NextStepsTemplate
          key="missing_premier_credentials"
          title="Unlock nearby shifts"
          content="Upload credentials to access more shifts."
          footnote={formatRecommendedCredentialsFootnote(recommendedCredentials)}
          icon={icons.unlock}
          to={to}
          color="default"
        />
      ) : null;
    }

    const recommendedCount =
      recommendedCredentials?.conditionCount ?? options.recommendedConditions?.length ?? 0;

    return isMissingRecommended && recommendedCount ? (
      <NextStepsTemplate
        key="missing_premier_credentials"
        content={
          platform === 'web'
            ? 'Complete your recommended credentials to unlock more opportunities, early access, and higher paying shifts.'
            : 'Complete your recommended credentials to unlock more shifts and higher rates.'
        }
        title="Unlock more shifts"
        icon={icons.award}
        to={to}
        color="default"
      />
    ) : null;
  };

  map['days_until_debit_card_expires' as keyof T] = (daysUntilExpiration) =>
    shouldRenderFor(['marketplace'], options.proType) &&
    typeof daysUntilExpiration === 'number' &&
    daysUntilExpiration < 14 ? (
      <NextStepsTemplate
        key="days_until_debit_card_expires"
        content="Update your expiring debit card."
        badge="Needs Attention"
        icon={icons.triangleExclamation}
        to={
          platform === 'web'
            ? { pathname: '/settings/financial/payout-method', state: { selected: 3 } }
            : { pathname: 'PayoutMethod' }
        }
        color="error"
      />
    ) : null;

  map['campaigns_status' as keyof T] = (data) =>
    isNonEmptyArray(data) ? <CampaignsNextStepCards key="campaigns_status" data={data} /> : null;

  map['pending_pro_pool_invite' as keyof T] = (data) =>
    Boolean(data) && options.isProPoolEnabled
      ? data.map((invite: PendingProPoolInvite) => (
          <ProPoolInvite
            key={`pending_pro_pool_invite_${invite.professional_qualification_id}`}
            locationId={invite.location_id}
            facilityName={invite.location_name}
            professionalQualificationId={invite.professional_qualification_id}
            positionNames={invite.position_names}
          />
        ))
      : null;

  map['pending_pro_pool_onboarding' as keyof T] = (data) =>
    Boolean(data) && options.isProPoolEnabled
      ? data.map((onboarding: PendingProPoolOnboarding) => (
          <ProPoolOnboarding
            key={`pending_pro_pool_onboarding_${onboarding.location_id}`}
            poolId={onboarding.pool_id}
            locationId={onboarding.location_id}
            facilityName={onboarding.location_name}
            positionIds={onboarding.position_ids}
            positionNames={onboarding.position_names}
          />
        ))
      : null;

  map['pending_pro_pool_orientation' as keyof T] = (data) =>
    data && options.isProPoolAutomationEnabled ? (
      <ProPoolOrientation
        key="pending_pro_pool_orientation"
        locationName={data.location_name}
        poolId={data.pool_id}
        positionNames={data.position_names}
        shiftType={data.shift_type}
      />
    ) : null;

  map['finished_jobs_count' as keyof T] = (data) =>
    shouldRenderFor(['marketplace'], options.proType) &&
    typeof data === 'number' &&
    data < FINISHED_JOBS_THRESHOLD ? (
      <SetYourselfUp
        key="finished_jobs_count"
        title="Set yourself up for success"
        body="Learn how to find and book shifts with Medely."
        iconName="light-info-circle"
      />
    ) : null;

  map['get_ready' as keyof T] = (data) =>
    data && shouldRenderFor(['marketplace'], options.proType) ? <GetReady key="get_ready" /> : null;

  map['set_yourself_up' as keyof T] = (data) =>
    data && shouldRenderFor(['marketplace'], options.proType) ? (
      <SetYourselfUp key="set_yourself_up" />
    ) : null;

  map['missing_w2_per_diem_onboarding' as keyof T] = (data) =>
    shouldRenderFor(['marketplace'], options.proType) && data ? (
      <NextStepsTemplate
        key="missing_w2_per_diem_onboarding"
        icon={icons.fileInvoiceDollar}
        title="W-2 Shifts"
        content={
          platform === 'web'
            ? 'Complete your W-2 paperwork for eligibility to work W-2 shifts offered by Frontline Health.'
            : 'Complete your W-2 paperwork on our website for eligibility to work W-2 shifts offered by Frontline Health.'
        }
        color="info"
        to={platform === 'web' ? '/onboarding/w2' : { pathname: 'GoToW2Onboarding' }}
      />
    ) : null;

  map['assignments_missing_conditions' as keyof T] = (data) =>
    isNonEmptyArray(data) ? (
      <NextStepsTemplate
        key="assignments_missing_conditions"
        icon={icons.triangleExclamation}
        badge="Needs Attention"
        content={
          platform === 'web'
            ? 'Finish onboarding for your upcoming assignment.'
            : 'Finish onboarding on our website for your upcoming assignment.'
        }
        color="error"
        to={
          platform === 'web'
            ? `/assignments/${data[0]}/onboarding`
            : { pathname: 'RedirectsToWebOnboarding' }
        }
        external={platform === 'mobile' ? true : false}
      />
    ) : null;

  map['enable_location_services' as keyof T] = (data) =>
    shouldRenderFor(['marketplace', 'agency'], options.proType) && data ? (
      <NextStepsTemplate
        key="enable_location_services"
        icon={platform === 'web' ? icons.locationCrosshairs : icons.location}
        title="Enable location services"
        content="Location sharing is required to clock in for your next shift. Enable location services now."
        color="default"
        onPress={options.handleAllow}
      />
    ) : null;

  map['pending_shift_swaps' as keyof T] = (data) =>
    shouldRenderFor(['irp'], options.proType) && isNonEmptyArray(data) && isShiftRequestGateOpen ? (
      <ShiftSwapCard
        name={data[0]?.requester_account_info?.name}
        shiftRequestsNumber={data?.length}
      />
    ) : null;

  map['approved_rate_changes' as keyof T] = (data) =>
    isNonEmptyArray(data)
      ? sortApprovedRateChangesByDecidedAt(data).map((item) => (
          <ApprovedRateChangeCard
            key={`approved_rate_changes-${item.shift_request_id}`}
            item={item}
          />
        ))
      : null;

  map['scheduling_preferences' as keyof T] = (data) =>
    data ? <SchedulerPreferencesCard key="scheduler_preferences_card" /> : null;

  //TODO: Move to MP once DisputeCardWrapper is migrated
  // map['disputes_waiting_on_professional' as keyof T] = (data) =>
  //   isNonEmptyArray(data) ? (
  //     <ExpiringConditions
  //       title="Disputes Waiting on You"
  //       severity="info"
  //       onPress={() => options.onTrackClick?.('disputes_waiting_on_professional' as keyof T, 1)}
  //     />
  //   ) : null;

  //TODO: Move to MP once MultipleRequestsFromFacility is migrated
  // map['active_jobs_requesting_to_work_facility_ids' as keyof T] = (data) =>
  //   isNonEmptyArray(data) ? (
  //     <ExpiringConditions
  //       title="Active Job Requests"
  //       severity="info"
  //       onPress={() =>
  //         options.onTrackClick?.('active_jobs_requesting_to_work_facility_ids' as keyof T, 1)
  //       }
  //     />
  //   ) : null;

  return map;
}
